import { AddCircleOutlined } from "@mui/icons-material";
import BlockOutlined from "@mui/icons-material/BlockOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import { Box, MenuItem, TextField, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemValidationTextField } from "../../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { EmptyState } from "../../../../../components/UIToolkit/molecules/EmptyState";
import SystemDialog from "../../../../../components/UIToolkit/Popups/Dialog";
import type { MultiSortOption } from "../../../../../components/UIToolkit/Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid } from "../../../../../components/UIToolkit/Tables/SystemDataGrid";
import { useChosenDepartment } from "../../../../../contexts/ChosenDepartment";
import { useErrorSnackbar } from "../../../../../hooks/useErrorSnackbar";
import { ExternalSource, type HrisIgnoreCategory, type HrisIgnoredValueRow } from "../../../../../utils/models";
import { useCreateHrisIgnoredValue, useDeleteHrisIgnoredValue, useHrisIgnoredValues } from "./hrisMappingHooks";
import { ConfirmRemoveIgnoreDialog, HoverActionCell } from "./utils";

interface IgnoredValueListGridProps {
	category: HrisIgnoreCategory;
	addButtonLabelKey: string;
	addDialogTitleKey: string;
	searchPlaceholderKey: string;
	localStorageKey: string;
	dataTestId: string;
}

export const IgnoredValueListGrid: FC<IgnoredValueListGridProps> = ({
	category,
	addButtonLabelKey,
	addDialogTitleKey,
	searchPlaceholderKey,
	localStorageKey,
	dataTestId,
}) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const departmentId = chosenDepartment.department_id;
	const { data: ignoredRows, isLoading, isError } = useHrisIgnoredValues(departmentId);
	const createMutation = useCreateHrisIgnoredValue(departmentId);
	const deleteMutation = useDeleteHrisIgnoredValue(departmentId);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [rawValueInput, setRawValueInput] = useState("");
	const [sourceInput, setSourceInput] = useState<string>("");
	const [pendingDelete, setPendingDelete] = useState<HrisIgnoredValueRow | null>(null);

	const rows = useMemo<HrisIgnoredValueRow[]>(() => (ignoredRows ?? []).filter((r) => r.category === category), [ignoredRows, category]);

	const confirmDelete = useCallback(() => {
		if (!pendingDelete) return;
		deleteMutation.mutate(pendingDelete.id, { onError: (err) => showErrorSnackbar(t("hris.mapping.deleteError"), {}, err) });
	}, [deleteMutation, pendingDelete, showErrorSnackbar, t]);

	const closeDialog = useCallback(() => {
		setDialogOpen(false);
		setRawValueInput("");
		setSourceInput("");
	}, []);

	const handleAdd = useCallback(() => {
		const trimmed = rawValueInput.trim().toLowerCase();
		if (!trimmed) return;
		createMutation.mutate(
			{
				category,
				rawValue: trimmed,
				departmentId,
				source: sourceInput || null,
			},
			{
				onError: (err) => showErrorSnackbar(t("hris.mapping.saveError"), {}, err),
				onSuccess: () => closeDialog(),
			},
		);
	}, [category, closeDialog, createMutation, departmentId, rawValueInput, showErrorSnackbar, sourceInput, t]);

	const sortOptions: MultiSortOption<HrisIgnoredValueRow>[] = useMemo(
		() => [
			{ label: t("hris.mapping.ignored.column.rawValue"), sortColumns: ["rawValue"] },
			{ label: t("hris.mapping.ignored.column.source"), sortColumns: ["source"] },
			{ label: t("hris.mapping.ignored.column.scope"), sortColumns: ["departmentId"] },
		],
		[t],
	);

	const columns: GridColDef<HrisIgnoredValueRow>[] = useMemo(
		() => [
			{
				field: "rawValue",
				headerName: t("hris.mapping.ignored.column.rawValue"),
				flex: 1,
				minWidth: 200,
			},
			{
				field: "source",
				headerName: t("hris.mapping.ignored.column.source"),
				flex: 1,
				minWidth: 140,
				renderCell: (params) => params.row.source ?? t("hris.mapping.ignored.allSources"),
			},
			{
				field: "departmentId",
				headerName: t("hris.mapping.ignored.column.scope"),
				width: 120,
				renderCell: (params) => (params.row.departmentId === null ? t("hris.mapping.ignored.scope.global") : t("hris.mapping.ignored.scope.dept")),
			},
			{
				field: "action",
				headerName: "",
				width: 80,
				sortable: false,
				align: "center",
				headerAlign: "center",
				display: "flex",
				renderCell: (params) => {
					const isGlobal = params.row.departmentId === null;
					return (
						<HoverActionCell
							show={true}
							actionIcon={<DeleteOutlineIcon fontSize="small" color={isGlobal ? "disabled" : "error"} />}
							tooltip={isGlobal ? t("hris.mapping.ignored.global") : t("hris.mapping.ignored.unignore")}
							onAction={isGlobal ? () => undefined : () => setPendingDelete(params.row)}
						/>
					);
				},
			},
		],
		[t],
	);

	const canSubmit = rawValueInput.trim() !== "";

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1, minHeight: 0 }}>
			<SystemDataGrid<HrisIgnoredValueRow>
				rows={rows}
				columns={columns}
				getRowId={(row) => row.id}
				searchableColumns={["rawValue", "source"]}
				searchPlaceholder={t(searchPlaceholderKey)}
				sortingOptions={sortOptions}
				defaultSortState={[{ sortColumns: ["rawValue"], sortOrder: "asc" }]}
				localStorageKey={localStorageKey}
				loading={isLoading}
				error={isError}
				containedHeight
				emptyState={
					<EmptyState
						icon={<BlockOutlined />}
						title={t("hris.mapping.ignored.empty.title")}
						hint={t("hris.mapping.ignored.empty.hint")}
						action={
							<SystemButton
								buttonVariant="secondary"
								size="small"
								leadingIcon={<AddCircleOutlined fontSize="small" />}
								onClick={() => setDialogOpen(true)}
								data-testid={`${dataTestId}-empty-add`}
							>
								{t(addButtonLabelKey)}
							</SystemButton>
						}
					/>
				}
				leadingToolbarNodes={
					<SystemButton buttonVariant="secondary" onClick={() => setDialogOpen(true)} sx={{ alignSelf: "flex-start" }} data-testid={`${dataTestId}-add`}>
						{t(addButtonLabelKey)}
					</SystemButton>
				}
				dataTestId={dataTestId}
			/>
			<SystemDialog
				open={dialogOpen}
				onClose={closeDialog}
				title={t(addDialogTitleKey)}
				body={
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
						<SystemValidationTextField
							autoFocus
							fullWidth
							label={t("hris.mapping.ignored.add.dialog.rawValue")}
							value={rawValueInput}
							onChange={(e) => setRawValueInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" && canSubmit) {
									e.stopPropagation();
									e.preventDefault();
									handleAdd();
								}
							}}
						/>
						<TextField
							select
							fullWidth
							label={t("hris.mapping.ignored.add.dialog.source")}
							value={sourceInput}
							onChange={(e) => setSourceInput(e.target.value)}
						>
							<MenuItem value="">
								<em>{t("hris.mapping.ignored.add.dialog.sourceAll")}</em>
							</MenuItem>
							{Object.values(ExternalSource).map((s) => (
								<MenuItem key={s} value={s}>
									{s}
								</MenuItem>
							))}
						</TextField>
						<Typography variant="caption" color="text.secondary">
							{t("hris.mapping.ignored.add.dialog.helper")}
						</Typography>
					</Box>
				}
				confirmDisabled={!canSubmit}
				onConfirm={handleAdd}
			/>
			<ConfirmRemoveIgnoreDialog
				open={pendingDelete !== null}
				rawValue={pendingDelete?.rawValue ?? ""}
				onClose={() => setPendingDelete(null)}
				onConfirm={confirmDelete}
			/>
		</Box>
	);
};
